import { computed, effect, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormRecord } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { EMPTY, map, Observable, take, timer } from 'rxjs';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { LoadingBarService } from 'src/app/services/loading-bar/loading-bar.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { getInternationalCsvSeparator, formatDateForExcel, formatInternationalNumber } from 'src/app/shared/constants/localization.constant';
import { PRICE_TYPES } from 'src/app/shared/constants/market.constant';
import { ChartData } from 'src/app/shared/models/chart.models';
import { FlipLocationStatus, FlipperConfiguration, FlipsStatus, MarketFlip, MarketFlipHistoryStats } from 'src/app/shared/models/flipper.model';
import { Item } from 'src/app/shared/models/items.model';

@Injectable({
  providedIn: 'root',
})
export class FlipperStateService {
  currentDateTime = toSignal(timer(0, 5000).pipe(map(() => new Date())), { initialValue: new Date() });

  // MARK: STATS PROPS
  chartData = signal<{ data: ChartData[]; name: string }[]>([]);
  flipStatus = signal<FlipsStatus[]>([]);
  groupedFlipsLocationStatus = signal<{ [key: string]: FlipLocationStatus[] }>({});
  flipHistoryStats = signal<MarketFlipHistoryStats[]>([]);

  // MARK: LOADING PROPS
  isLoadingFlipsCounters = signal(false);
  isUpdateingFlipsLiveStats = signal(false);
  isUpdateingFlipsHistoryStats = signal(false);
  isLoadingFlips = signal(false);
  isUpdatingUpgradePrices = signal(false);
  isLoadingUpgradePrices = signal(false);
  isSavingConfig = signal(false);
  isSavingPrices = signal(false);
  isConsumingFlip = signal(false);

  isLoadingAny = computed(() => {
    return (
      this.isLoadingFlipsCounters() ||
      this.isUpdateingFlipsHistoryStats() ||
      this.isUpdateingFlipsLiveStats() ||
      this.isLoadingFlips() ||
      this.isUpdatingUpgradePrices() ||
      this.isLoadingUpgradePrices() ||
      this.isSavingConfig() ||
      this.isSavingPrices() ||
      this.isConsumingFlip()
    );
  });

  isLoadingAnyStats = computed(() => {
    return this.isLoadingFlipsCounters() || this.isUpdateingFlipsLiveStats() || this.isUpdateingFlipsHistoryStats();
  });

  // MARK: SESSION PROPS
  consumedFlipsDataSource: MatTableDataSource<MarketFlip> = new MatTableDataSource<MarketFlip>(this.userSettingsService.getUserConsumedFlips());
  consumedFlipsSort!: MatSort;
  consumedFlipsPaginator!: MatPaginator;

  // MARK: UPGRADE RESOURCES PROPS
  upgradeResources: { item: Item; availableAmount: number | null }[] = [];
  resourcesAreSet: boolean = false;
  availableAmountsFormRecord = new FormRecord<FormControl<number | null>>({});

  // MARK: FLIPS PROPS
  flips: MarketFlip[] = [];
  hasInitializedFlips = false;
  previousServer: string = '';
  flipsDataSource: MatTableDataSource<MarketFlip> = new MatTableDataSource<MarketFlip>(this.flips);

  missedFlipsCount: number = 0;
  missedFlipsProfit: number = 0;

  // MARK: FORM PROPS
  resourcesFormRecord = new FormRecord<FormControl<number | null>>({});

  flipperFormGroup = new FormGroup({
    royalBuyLocationSearchText: new FormControl(''),
    smugglersBuyLocationSearchText: new FormControl(''),
    buyLocationForm: new FormControl(this.userSettingsService.getUserFlipperConfig().buyLocations),
    smugglerBuyLocation: new FormControl(this.userSettingsService.getUserFlipperConfig().smugglerBuyLocation),
    sellLocationForm: new FormControl(this.userSettingsService.getUserFlipperConfig().sellLocations),
    minProfitInput: new FormControl(this.userSettingsService.getUserFlipperConfig().minProfit),
    minProfitPercentage: new FormControl(this.userSettingsService.getUserFlipperConfig().minProfitPercentage),
    maxBuyOrderAge: new FormControl(this.userSettingsService.getUserFlipperConfig().maxBuyOrderAge),
    maxSellorderAge: new FormControl(this.userSettingsService.getUserFlipperConfig().maxSellOrderAge),
    hideUpgrades: new FormControl(this.userSettingsService.getUserFlipperConfig().hideUpgrades),
    userFlips: new FormControl(this.userSettingsService.getUserFlipperConfig().userFlips),
    hideConsumed: new FormControl(this.userSettingsService.getUserFlipperConfig().hideConsumed),
    hideDuped: new FormControl(this.userSettingsService.getUserFlipperConfig().hideDuped),
    showNonPremium: new FormControl(this.userSettingsService.getUserFlipperConfig().showNonPremium),
    tiers: new FormControl(this.userSettingsService.getUserFlipperConfig().tiers),
    upgradeFroms: new FormControl(this.userSettingsService.getUserFlipperConfig().upgradeFroms),
    upgradeTos: new FormControl(this.userSettingsService.getUserFlipperConfig().upgradeTos),
    maxUpgradeResourcesCount: new FormControl(this.userSettingsService.getUserFlipperConfig().maxUpgradeResourcesCount),
    minBuyOrderAmount: new FormControl(this.userSettingsService.getUserFlipperConfig().minBuyOrderAmount),
    hideMissingUpgrades: new FormControl(this.userSettingsService.getUserFlipperConfig().hideMissingUpgrades),
    resources: this.resourcesFormRecord,
    resourceBuyLocationForm: new FormControl(this.userSettingsService.getUserFlipperConfig().buyLocations),
    resourcesPriceType: new FormControl(this.userSettingsService.getUserFlipperConfig().resourcePriceType),
    resourceAverageDays: new FormControl(this.userSettingsService.getUserFlipperConfig().resourceAverageDays),
  });

  // MARK: CONSTRUCTOR
  constructor(
    private userSettingsService: UserSettingsService,
    private loadingBarService: LoadingBarService,
    private aoDataService: AoDataGetterService,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog
  ) {
    effect(() => {
      this.loadingBarService.setLoading(this.isLoadingAny());
    });
  }

  // MARK: setupConsumedFlipsDataSource
  setupConsumedFlipsDataSource() {
    this.consumedFlipsDataSource = new MatTableDataSource<MarketFlip>(this.userSettingsService.getUserConsumedFlips());
    if (this.consumedFlipsSort) {
      this.consumedFlipsSort.active = this.consumedFlipsSort.active ? this.consumedFlipsSort.active : 'consumedAt';
      this.consumedFlipsSort.direction = this.consumedFlipsSort.direction ? this.consumedFlipsSort.direction : 'asc';
      this.consumedFlipsDataSource.sort = this.consumedFlipsSort;
      this.consumedFlipsDataSource.sort.sortChange.emit(this.consumedFlipsDataSource.sort);
    }
    this.consumedFlipsDataSource.sortingDataAccessor = (item: MarketFlip, property) => {
      switch (property) {
        case 'itemUsCompleteName':
          return item.item?.localizedName ?? item.item?.uniqueName ?? '';
        case 'consumedAt':
          return item.consumedAt ? item.consumedAt.getTime() : new Date(0).getTime();
        case 'buyOrderUnitPriceSilver':
          return item.buyOrder.unitPriceSilver;
        case 'sellOrderUnitPriceSilver':
          return item.sellOrder.unitPriceSilver;
        case 'upgradeCost':
          return item.upgradeCost;
        case 'profitPremium':
          return item.profitPremium;
        case 'profitPercentagePremium':
          return item.profitPercentagePremium;
        case 'profitNonPremium':
          return item.profitNonPremium;
        case 'profitPercentageNonPremium':
          return item.profitPercentageNonPremium;
        default:
          return item.profitPremium;
      }
    };

    this.consumedFlipsDataSource.paginator = this.consumedFlipsPaginator;
  }

  // MARK: setupUpgradesResources
  setupUpgradeResources(resources: Item[]) {
    this.upgradeResources = resources.map((item) => ({ item, availableAmount: null }));
    this.upgradeResources
      .map((x) => x.item)
      .forEach((resource) => {
        this.resourcesFormRecord.addControl(resource.uniqueName, new FormControl(0));
        this.availableAmountsFormRecord.addControl(
          resource.uniqueName,
          new FormControl(this.upgradeResources.find((x) => x.item.uniqueName === resource.uniqueName)?.availableAmount ?? null)
        );
      });
    const order = ['ESSENCE', 'TOKEN', 'RUNE', 'SOUL', 'RELIC'];

    this.upgradeResources.sort((a, b) => {
      const indexA = order.indexOf(a.item.resourceType!);
      const indexB = order.indexOf(b.item.resourceType!);
      return indexA - indexB;
    });
    this.resourcesAreSet = true;
  }

  // MARK: saveFlipperConfig
  saveFlipperConfig() {
    if (this.userSettingsService.isLoggedIn) {
      this.isSavingConfig.set(true);
      const config = new FlipperConfiguration();
      config.buyLocations = this.flipperFormGroup.controls.buyLocationForm.value ?? [];
      config.smugglerBuyLocation = this.flipperFormGroup.controls.smugglerBuyLocation.value ?? null;
      config.sellLocations = this.flipperFormGroup.controls.sellLocationForm.value ?? [];
      config.minProfit = this.flipperFormGroup.controls.minProfitInput.value ?? 5000;
      config.minProfitPercentage = this.flipperFormGroup.controls.minProfitPercentage.value ?? 0;
      config.maxBuyOrderAge = this.flipperFormGroup.controls.maxBuyOrderAge.value ?? null;
      config.maxSellOrderAge = this.flipperFormGroup.controls.maxSellorderAge.value ?? null;
      config.hideUpgrades = this.flipperFormGroup.controls.hideUpgrades.value ?? false;
      config.userFlips = this.flipperFormGroup.controls.userFlips.value ?? false;
      config.hideConsumed = this.flipperFormGroup.controls.hideConsumed.value ?? false;
      config.hideDuped = this.flipperFormGroup.controls.hideDuped.value ?? false;
      config.showNonPremium = this.flipperFormGroup.controls.showNonPremium.value ?? false;
      config.tiers = this.flipperFormGroup.controls.tiers.value ?? [];
      config.upgradeFroms = this.flipperFormGroup.controls.upgradeFroms.value ?? [];
      config.upgradeTos = this.flipperFormGroup.controls.upgradeTos.value ?? [];
      config.maxUpgradeResourcesCount = this.flipperFormGroup.controls.maxUpgradeResourcesCount.value;
      config.minBuyOrderAmount = this.flipperFormGroup.controls.minBuyOrderAmount.value;
      config.hideMissingUpgrades = this.flipperFormGroup.controls.hideMissingUpgrades.value ?? false;
      config.resourcePriceType = this.flipperFormGroup.controls.resourcesPriceType.value ?? PRICE_TYPES.sell;
      config.resourceAverageDays = this.flipperFormGroup.controls.resourceAverageDays.value ?? 7;
      // We'll keep the same existing prices and amounts for the saved config
      config.upgradeResourcesPrices = this.userSettingsService.getUserFlipperConfig().upgradeResourcesPrices;
      config.upgradeResourcesAmounts = this.userSettingsService.getUserFlipperConfig().upgradeResourcesAmounts;

      this.userSettingsService.updateUserFlipperConfig(config);
      setTimeout(() => {
        this.isSavingConfig.set(false);
      }, 1000);
    } else {
      this.snackBar.open(`You must be Logged In to save!`, undefined, { duration: 2000 });
    }
  }

  // MARK: saveUpgradePricesAndAmountsToDb
  saveUpgradePricesAndAmountsToDb() {
    this.isSavingPrices.set(true);

    const dialogRef = this.matDialog.open(DialogComponent, {
      width: '250px',
      data: { title: 'Confirm Action', message: `Are you sure you want to save the runes data? This action will overwrite the existing data.` },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(async (result) => {
        if (result) {
          if (this.userSettingsService.isLoggedIn) {
            const config = this.userSettingsService.getUserFlipperConfig();

            config.upgradeResourcesPrices = Object.keys(this.resourcesFormRecord.controls).map((key) => ({
              uniqueName: key,
              price: this.resourcesFormRecord.controls[key].value ?? 0,
            }));
            config.upgradeResourcesAmounts = Object.keys(this.availableAmountsFormRecord.controls).map((key) => ({
              uniqueName: key,
              amount: this.availableAmountsFormRecord.controls[key].value ?? null,
            }));
            // Update the user's flipper config so if he reloads from db the prices they actually update (since it doesnt actually call the db)
            this.userSettingsService.getUserFlipperConfig().upgradeResourcesPrices = config.upgradeResourcesPrices;
            this.userSettingsService.getUserFlipperConfig().upgradeResourcesAmounts = config.upgradeResourcesAmounts;
            this.userSettingsService.updateUserFlipperConfig(config);
            setTimeout(() => {
              this.isSavingPrices.set(false);
            }, 1000);
          } else {
            this.snackBar.open(`You must be Logged In to save!`, undefined, { duration: 2000 });
            this.isSavingPrices.set(false);
          }
        } else {
          this.snackBar.open(`Action canceled by the user.`, undefined, { duration: 2000 });
          this.isSavingPrices.set(false);
        }
      });
  }

  // MARK: loadUpgradeResourcesPricesAndAmountsFromDb
  loadUpgradeResourcesPricesAndAmountsFromDb() {
    this.isLoadingUpgradePrices.set(true);

    const dialogRef = this.matDialog.open(DialogComponent, {
      width: '250px',
      data: { title: 'Confirm Action', message: `Are you sure you want to load your saved runes data? This action will overwrite the current website data.` },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        var prices = this.userSettingsService.getUserFlipperConfig().upgradeResourcesPrices;
        var amounts = this.userSettingsService.getUserFlipperConfig().upgradeResourcesAmounts;
        prices.forEach((price) => {
          this.resourcesFormRecord.controls[price.uniqueName]?.setValue(price.price);
        });
        amounts.forEach((amount) => {
          this.availableAmountsFormRecord.controls[amount.uniqueName]?.setValue(amount.amount);
        });
        setTimeout(() => {
          this.isLoadingUpgradePrices.set(false);
        }, 1000);
      } else {
        this.snackBar.open(`Action canceled by the user.`, undefined, { duration: 2000 });
        this.isLoadingUpgradePrices.set(false);
      }
    });
  }

  // MARK: updateResourcePrices
  updateResourcePrices(): Observable<void> {
    this.isUpdatingUpgradePrices.set(true);
    if (this.resourcesAreSet) {
      if (this.flipperFormGroup.controls.resourcesPriceType.value === PRICE_TYPES.average) {
        this.aoDataService
          .getHistory(this.upgradeResources.map((x) => x.item.uniqueName))
          .pipe(take(1))
          .subscribe((histories) => {
            const buyLocationIndices = this.flipperFormGroup.controls.resourceBuyLocationForm.value?.map((x) => x.index);
            this.upgradeResources
              .map((x) => x.item)
              .forEach((resource) => {
                let filteredHistories = histories.filter((history) => {
                  const matchesUniqueName = history.item?.uniqueName === resource.uniqueName;
                  const locationIncluded = buyLocationIndices?.length ? buyLocationIndices.includes(history.location?.index ?? 0) : true;
                  return matchesUniqueName && locationIncluded;
                });
                let historyData = filteredHistories.flatMap((data) => data.data);
                let count = 0;
                let accPrice = 0;
                historyData.forEach((data) => {
                  if (!data) return;
                  if (
                    new Date().getTime() - data!.timestamp.getTime() <
                    1000 * 60 * 60 * 24 * (this.flipperFormGroup.controls.resourceAverageDays.value ?? 7)
                  ) {
                    count += data?.itemCount ?? 0;
                    accPrice += (data?.averagePrice ?? 0) * (data?.itemCount ?? 0);
                  }
                });
                let averagePrice = count > 0 ? accPrice / count : 0;
                this.resourcesFormRecord.controls[resource.uniqueName].setValue(Math.ceil(averagePrice));
              });
          });
      } else {
        this.aoDataService
          .getPrices(this.upgradeResources.map((x) => x.item.uniqueName))
          .pipe(take(1))
          .subscribe((pricesResponse) => {
            const prices = pricesResponse;

            this.upgradeResources
              .map((x) => x.item)
              .forEach((resource) => {
                const relevantPrices = prices.filter(
                  (price) =>
                    price.item.uniqueName === resource.uniqueName &&
                    (this.flipperFormGroup.controls.resourceBuyLocationForm.value?.length
                      ? this.flipperFormGroup.controls.resourceBuyLocationForm.value.includes(price.city)
                      : true)
                );
                let price = 0;
                if (this.flipperFormGroup.controls.resourcesPriceType?.value === PRICE_TYPES.sell) {
                  price = relevantPrices
                    .filter((price) => price.sellPriceMin > 0)
                    .map((price) => price.sellPriceMin)
                    .reduce((acc, curr, _, arr) => acc + curr / arr.length, 0);
                } else if (this.flipperFormGroup.controls.resourcesPriceType?.value === PRICE_TYPES.buy) {
                  price = relevantPrices
                    .filter((price) => price.buyPriceMax > 0)
                    .map((price) => price.buyPriceMax)
                    .reduce((acc, curr, _, arr) => acc + curr / arr.length, 0);
                }
                this.resourcesFormRecord.controls[resource.uniqueName].setValue(Math.ceil(price));
              });
          });
      }
    }
    setTimeout(() => {
      this.isUpdatingUpgradePrices.set(false);
    }, 1000);
    return EMPTY;
  }

  clickUpdateResourcePrices() {
    this.isUpdatingUpgradePrices.set(true);

    const dialogRef = this.matDialog.open(DialogComponent, {
      width: '250px',
      data: { title: 'Confirm Action', message: `Are you sure you want to update the runes prices? This will replace the current website prices.` },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.updateResourcePrices();
      } else {
        this.snackBar.open(`Action canceled by the user.`, undefined, { duration: 2000 });
        this.isUpdatingUpgradePrices.set(false);
      }
    });
  }

  // MARK: countOrderIsUsedInMultipleFlips
  countOrderIsUsedInMultipleFlips(orderId: number): number | null {
    const count = this.flips.filter((flip) => flip.buyOrder.id === orderId || flip.sellOrder.id === orderId).length;
    return count > 1 ? count : null;
  }

  // MARK: exportMarketFlipsToCSV
  exportMarketFlipsToCSV(marketFlips: MarketFlip[], _fileName: string): void {
    const separator = getInternationalCsvSeparator();
    // Sort the flips buy profit premium
    marketFlips.sort((a, b) => b.profitPremium - a.profitPremium);
    // Define CSV column headers
    const headers = [
      'Buy Item',
      'Sell Item',
      'Server',
      'Is Consumed',
      'Consumed At',
      'Buy Location',
      'Buy Price',
      'Buy Quality',
      'Sell Location',
      'Sell Price',
      'Sell Quality',
      'Is Upgrade',
      'Upgrade From',
      'Upgrade To',
      'UpgradeCost',
      'Profit Premium',
      'Profit Non-Premium',
      'Profit % Premium',
      'Profit % Non-Premium',
      'Upgrade 1 Item',
      'Upgrade 1 Amount',
      'Upgrade 1 Price',
      'Upgrade 2 Item',
      'Upgrade 2 Amount',
      'Upgrade 2 Price',
      'Upgrade 3 Item',
      'Upgrade 3 Amount',
      'Upgrade 3 Price',
    ].join(separator);

    // Generate CSV rows for each MarketFlip
    const rows = marketFlips
      .map((flip) => {
        let result = [
          flip.sellOrder.item?.localizedName ?? flip.sellOrder.item?.uniqueName,
          flip.buyOrder.item?.localizedName ?? flip.buyOrder.item?.uniqueName,
          flip.server,
          flip.isConsumed ? 'Yes' : 'No',
          flip.consumedAt ? formatDateForExcel(flip.consumedAt) : '',
          flip.sellOrder.location.displayName,
          formatInternationalNumber(flip.sellOrder.unitPriceSilver),
          flip.sellOrder.qualityLevel.name,
          flip.buyOrder.location.displayName,
          formatInternationalNumber(flip.buyOrder.unitPriceSilver),
          flip.buyOrder.qualityLevel.name,
          flip.isUpgrade ? 'Yes' : 'No',
          flip.upgradeFrom,
          flip.upgradeTo,
          formatInternationalNumber(flip.upgradeCost),
          formatInternationalNumber(flip.profitPremium),
          formatInternationalNumber(flip.profitNonPremium),
          formatInternationalNumber(flip.profitPercentagePremium, 4),
          formatInternationalNumber(flip.profitPercentageNonPremium, 4),
        ].join(separator);
        if (!flip.isUpgrade) {
          result += new Array(10).join(separator);
        } else {
          if (flip.upgradeFrom === 0) {
            for (let index = 0; index < 3; index++) {
              if (flip.upgradeResources[index]) {
                result += `${separator}${flip.upgradeResources[index].item?.localizedName ?? flip.upgradeResources[index].item?.uniqueName}${separator}${formatInternationalNumber(
                  flip.upgradeResources[index].count
                )}${separator}${formatInternationalNumber(flip.upgradeResources[index].price)}`;
              } else {
                result += `${separator}${separator}${separator}`;
              }
            }
          }
          if (flip.upgradeFrom === 1) {
            result += `${separator}${separator}${separator}`;
            for (let index = 0; index < 2; index++) {
              if (flip.upgradeResources[index]) {
                result += `${separator}${flip.upgradeResources[index].item?.localizedName ?? flip.upgradeResources[index].item?.uniqueName}${separator}${formatInternationalNumber(
                  flip.upgradeResources[index].count
                )}${separator}${formatInternationalNumber(flip.upgradeResources[index].price)}`;
              } else {
                result += `${separator}${separator}${separator}`;
              }
            }
          }
          if (flip.upgradeFrom === 2) {
            result += `${separator}${separator}${separator}${separator}${separator}${separator}`;
            for (let index = 0; index < 1; index++) {
              if (flip.upgradeResources[index]) {
                result += `${separator}${flip.upgradeResources[index].item?.localizedName ?? flip.upgradeResources[index].item?.uniqueName}${separator}${formatInternationalNumber(
                  flip.upgradeResources[index].count
                )}${separator}${formatInternationalNumber(flip.upgradeResources[index].price)}`;
              } else {
                result += `${separator}${separator}${separator}`;
              }
            }
          }
        }
        return result;
      })
      .join('\n');

    // Combine headers and rows
    const csvContent = [headers, rows].join('\n');

    // Export the CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = _fileName + '.csv';

    const link = document.createElement('a');
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}
